import { z } from 'zod';
import { Id, IsoDateTime, JsonObject, TenantId } from './common.ts';
import { ResourceScope } from './entitlement.ts';

/**
 * Principal kinds. Added by 21 step 0: `external` — an invited outside party (candidate, bidder, customer contact)
 * that authenticates by invitation token, holds only the work items assigned to it and the documents whose ACL names
 * it, never a pack role and never an approval route (brief 21 §1b.9, ADR-021).
 */
export const PrincipalKind = z
  .enum(['human', 'workload', 'service_mandate', 'external'])
  .meta({ id: 'PrincipalKind' });
export type PrincipalKind = z.infer<typeof PrincipalKind>;

/** An authenticated actor: a person, a workload, or a service acting under a mandate. */
export const Principal = z
  .object({
    /** Stable subject id from the identity provider (not necessarily a UUID). */
    id: z.string().min(1),
    kind: PrincipalKind,
    tenantId: TenantId,
    displayName: z.string(),
    roles: z.array(z.string()).default([]),
    /** Subjects used for ACL filtering of knowledge and artifacts (e.g. `procurement-staff`). */
    aclSubjects: z.array(z.string()).default([]),
    attributes: JsonObject.default({}),
  })
  .meta({ id: 'Principal' });
export type Principal = z.infer<typeof Principal>;

export const WorkloadRef = z
  .object({ id: z.string().min(1), name: z.string().min(1), version: z.string().min(1) })
  .meta({ id: 'WorkloadRef' });
export type WorkloadRef = z.infer<typeof WorkloadRef>;

/**
 * Trusted execution context, attached by infrastructure (never by the model or the caller's payload).
 * Carried between components as a signed JWT (`SignedExecutionContext`); sign/verify helpers live in the kernel.
 */
export const ExecutionContext = z
  .object({
    traceId: z.string().min(1),
    taskId: Id.optional(),
    tenantId: TenantId,
    domain: z.string().min(1),
    actor: Principal,
    workload: WorkloadRef,
    purpose: z.string().min(1),
    delegationRef: z.string().optional(),
    region: z.string().min(1),
    policyVersion: z.string().min(1),
    /** Added by 17a: resource scope of the work this context runs for (from the task or case record). */
    scope: ResourceScope.optional(),
    /**
     * Added by 21 step 0 (brief 21 §1b.10): the process activation this context acts for. Every process execution
     * context carries it — spawned tasks and direct `gateway.execute` service calls alike — so the tool gateway
     * resolves the pinned activity definition and enforces its `tools` scope. Set by infrastructure only.
     */
    activationId: z.string().min(1).max(200).optional(),
    /** Added by 21 step 0: the process instance of `activationId`. */
    processInstanceId: Id.optional(),
    issuedAt: IsoDateTime,
    expiresAt: IsoDateTime,
  })
  .meta({ id: 'ExecutionContext' });
export type ExecutionContext = z.infer<typeof ExecutionContext>;

/** Compact JWS (HS256 in the monolith) whose `ctx` claim is an `ExecutionContext`. */
export const SignedExecutionContext = z
  .string()
  .regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/, 'must be a compact JWS')
  .meta({ id: 'SignedExecutionContext' });
export type SignedExecutionContext = z.infer<typeof SignedExecutionContext>;

/**
 * Added by 21 step 0 (brief 21 §1, §1b.9; ADR-021): an invited external party. Lives in `core_identity_admin`,
 * authenticates by invitation token (stored hashed), expires at `expiresAt`, and is narrowed to `caseIds` and
 * `scope`. It satisfies a work item's holder check only when `item.assigneePrincipalId === id` (`act` implied for
 * that item only); it never receives a pack role. Revoked or expired → 401.
 */
export const ExternalPrincipal = z
  .object({
    id: z.string().min(1),
    email: z.email(),
    displayName: z.string().min(1).max(200),
    tenantId: TenantId,
    scope: ResourceScope.default({}),
    expiresAt: IsoDateTime,
    /** Principal id of the inviter. */
    invitedBy: z.string().min(1),
    caseIds: z.array(Id).min(1),
    status: z.enum(['active', 'revoked']).default('active'),
    revokedAt: IsoDateTime.optional(),
    revokedBy: z.string().min(1).optional(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'ExternalPrincipal' });
export type ExternalPrincipal = z.infer<typeof ExternalPrincipal>;

/** Body of `POST /externals` (inviter needs `act` on every listed case). */
export const InviteExternalBody = z
  .object({
    email: z.email(),
    displayName: z.string().min(1).max(200),
    caseIds: z.array(Id).min(1).max(20),
    scope: ResourceScope.optional(),
    /** Absolute expiry; default and maximum from configuration. */
    expiresAt: IsoDateTime.optional(),
    /** Notification template of the invitation (default `platform.external.invited`). */
    templateKey: z.string().min(1).max(200).optional(),
  })
  .meta({ id: 'InviteExternalBody' });
export type InviteExternalBody = z.infer<typeof InviteExternalBody>;

/** Response of `POST /externals`: the token is returned once (and sent through the Notifier); only its hash is stored. */
export const InviteExternalResponse = z
  .object({
    external: ExternalPrincipal,
    invitation: z.object({ token: z.string().min(16), expiresAt: IsoDateTime }),
  })
  .meta({ id: 'InviteExternalResponse' });
export type InviteExternalResponse = z.infer<typeof InviteExternalResponse>;

/**
 * Added by 21b (brief 21 §1b.9; decision recorded in STATUS 21b): the response `POST /externals` actually returns.
 * The invitation token is **never** echoed to the inviter: it is delivered only through the Notifier (template
 * `platform.external.invited`, param `token`), so the inviter cannot act as the external. The response carries the
 * external, the token expiry and the notification receipt. `InviteExternalResponse` (step 0) stays exported unused.
 */
export const InviteExternalResult = z
  .object({
    external: ExternalPrincipal,
    invitation: z.object({
      expiresAt: IsoDateTime,
      /** Status of the governed send that delivered the token (`accepted`, `delivered`, `failed`, `unknown`). */
      notification: z.object({
        notificationId: Id,
        status: z.enum(['accepted', 'delivered', 'failed', 'unknown']),
      }),
    }),
  })
  .meta({ id: 'InviteExternalResult' });
export type InviteExternalResult = z.infer<typeof InviteExternalResult>;

/**
 * Added by 21b: the invitation link an external opens: `<workbenchBaseUrl>/external#token=<token>`. The token is in
 * the URL **fragment**, never a query string, so it never reaches Referer headers or access logs (decision 2026-11-01).
 */
export function externalInvitationLink(workbenchBaseUrl: string, token: string): string {
  return `${workbenchBaseUrl.replace(/\/+$/, '')}/external#token=${encodeURIComponent(token)}`;
}

/** Added by 21b: prefix of external invitation tokens (`Authorization: Bearer ext_…`). */
export const EXTERNAL_TOKEN_PREFIX = 'ext_';

/** Added by 21b: `Principal.attributes` key holding an external principal's case ids (set by identity-admin only). */
export const EXTERNAL_CASE_IDS_ATTRIBUTE = 'externalCaseIds';

/** Added by 21b: the case ids an external principal is narrowed to (empty for every other kind). */
export function externalCaseIdsOf(principal: Pick<Principal, 'kind' | 'attributes'>): string[] {
  if (principal.kind !== 'external') return [];
  const raw = principal.attributes[EXTERNAL_CASE_IDS_ATTRIBUTE];
  return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [];
}
